
// export default PastMedicationForm;
import React, { useState, useCallback, useEffect } from 'react';
import TableComponent from '../../../components/Table/TableComponent';

import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell} from '@mui/material';
import { fetchPastMedications } from '../../../services/SubjectiveServices/PastMedicationformService';
import { useAuth } from '../../../context/AuthContext';
import { PatientHealth, PatientHealthsData, PrescriptionValue } from '../../../components/interface/SubjectiveInterfaces/PastMedicationInterface';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';

import TablePaginationComponent from '../../../components/TablePaginationComponent';
import IconButton, { ClearButton } from '../../../components/Buttons/SubjectiveIcons';
import SnackbarMessage from '../../../components/SnackBAr/SnackbarNotification';
import LoadingAnimation from '../../../components/LoadingAnimation';



interface PastMedicationFormProps {
    patientId: string;
}

const PastMedicationForm: React.FC<PastMedicationFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [pastMedications, setPastMedications] = useState<PatientHealthsData>({
        patient_healths: [],
        pagination: {
            total: 0,
            per_page: 10,
            current_page: 1,
            last_page: 1,
        }
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
  
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
   
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);


    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');


    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const data = pastMedications?.patient_healths || [];

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };


    const fetchPastMedicationsData = useCallback(async (initial = false) => {
        setLoading(true);
        setError('');
        try {
            const params: Record<string, any> = {
                limit: rowsPerPage,
                page: page + 1,
                user_id: patientId,
                searchkey: searchTerm,
                from: !initial ? dateRange.start : undefined,
                to: !initial ? dateRange.end : undefined,
                order_by: 'created_at',
                dir: 2,
                slug: 'prescription',
            };

            const data = await fetchPastMedications(token!, params);

            // Map the fetched data to PatientHealth[]
            const fetchedMedications: PatientHealth[] = data.data.patient_healths.map(item => {
                return {
                    id: item.id,
                    patient_id: item.patient_id,
                    slug: item.slug,
                    consult_id: item.consult_id,
                    values: Array.isArray(item.values) ? item.values.map((value: PrescriptionValue) => {
                        const additionalInfo = value.additional_info || {};
                        return {
                            total_qty: value.total_qty,
                            medicine_name: value.medicine_name,
                            medicine_type: value.medicine_type,
                            medicine_input: value.medicine_input,
                            medicine_taken: value.medicine_taken,
                            medicine_takenat: value.medicine_takenat,
                            medicine_duration: value.medicine_duration,
                            medicine_duration_ip: value.medicine_duration_ip,
                            medicine_instruction: value.medicine_instruction,
                            medicine_duration_type: value.medicine_duration_type,
                            total_medicine_duration: value.total_medicine_duration,
                            dosage: value.dosage,
                            additional_info: {
                                start_date: additionalInfo.start_date || null,
                                is_Active: additionalInfo.is_Active || false,
                                end_date: additionalInfo.end_date || null,
                                route: additionalInfo.route || null,
                            },
                        };
                    }) : [], // Default to empty array if item.values is not an array
                    created_at: item.created_at,
                    freeze: item.freeze
                };
            });
            console.log('Fetched data:', data);

            // Setting the state to an array of PatientHealth
            const patientHealthsData: PatientHealthsData = {
                patient_healths: fetchedMedications,
                pagination: {
                    total: data.data.pagination.total,
                    per_page: data.data.pagination.per_page,
                    current_page: data.data.pagination.current_page,
                    last_page: data.data.pagination.last_page,
                }
            };

            // Setting the state to PatientHealthsData
            setPastMedications(patientHealthsData);


            setShowClearFilterButton(!initial || searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== ''));

        } catch (error) {
            setError('Failed to fetch past medications.');
            console.error('Error fetching past medications', error);
            showSnackbar('Failed to fetch past medications.', 'error');
        } finally {
            setLoading(false); 
        }
    }, [searchTerm, dateRange, page, rowsPerPage, patientId]);




    useEffect(() => {
        fetchPastMedicationsData(true);
    }, [fetchPastMedicationsData]);

    useEffect(() => {
        const isSearchable = searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== '');
        setIsButtonEnabled(isSearchable);
    }, [searchTerm, dateRange]);

    const handleSearch = useCallback(() => {
        if (dateRange.start && dateRange.end) {
            setPage(0);
            setLoading(true);
            fetchPastMedicationsData().finally(() => setLoading(false));;
        } else {
            console.warn('Please select both from date and to date.');
            showSnackbar('Please select both from date and to date.', 'warning');
        }
    }, [fetchPastMedicationsData, dateRange]);

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ start: '', end: '' });
        setPage(0);
        fetchPastMedicationsData(true);
    };


    const renderRow = (item: PatientHealth, index: number) => {
        // Ensure that the values array exists and has at least one item
        const medication = Array.isArray(item.values) && item.values.length > 0 ? item.values[0] : null;
        console.log(medication?.additional_info);
    
       
    
        return (
            <TableRow key={item.id || index}>
                <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {page * rowsPerPage + index + 1}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {medication?.medicine_name || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {medication?.medicine_taken?.m || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
                    {medication?.medicine_taken?.a || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {medication?.medicine_taken?.e || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {medication?.medicine_taken?.n || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {medication?.medicine_takenat || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>
                    {medication?.total_qty || '-'}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {`${medication?.total_medicine_duration || ''} ${medication?.medicine_duration_type || ''}` || '-'}
                </TableCell>
                {/* Uncomment and add optional chaining if needed */}
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif'  , fontSize: '1rem' }}>
                    {medication?.additional_info?.is_Active || '-'}
                </TableCell>
                
            </TableRow>
        );
    };
    


    return (
        <div>
            <div className="flex flex-wrap items-center gap-4 w-full pb-4">
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                    <DateRangePicker
                        startDateId="pastMedication_Start_Date"
                        endDateId="pastMedication_End_Date"
                        value={{ startDate: dateRange.start, endDate: dateRange.end }}
                        onChange={dates => {
                            setDateRange({ start: dates.startDate || '', end: dates.endDate || '' });
                        }}
                    />
                    <div className="flex items-center gap-2 shrink-0">
                        <IconButton
                            onClick={handleSearch}
                            disabled={!isButtonEnabled}
                            icon={<SearchIcon />}
                        />
                        {showClearFilterButton && (
                            <ClearButton onClick={handleClearFilters} />
                        )}
                    </div>
                </div>
            </div>

            {loading} {/* Show spinner when loading */}


            {loading ? (
                <div className="text-center my-4"><LoadingAnimation /></div>
            ) : (
                <div>
                    <TableComponent
                        headers={['S.No', 'Medicine Name', 'M ', 'A', 'E', ' N', 'BF/AF', 'Quantity', 'Duration', 'Status']}
                        data={data}
                        loading={loading}
                        error={error}
                        renderRow={renderRow}
                    />
                   
                </div>
            )}

            <TablePaginationComponent
                count={pastMedications.pagination.total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />
           {/* Snackbar Component */}
           <SnackbarMessage
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setSnackbarOpen(false)}
                /> 
        </div>
    );
};

export default PastMedicationForm;
